/*
  Where the sign-in token lives, and for how long.

  Two kinds of session, chosen at sign-in:

  - remembered: the token goes to localStorage and stays there, as before.
  - not remembered: it goes to sessionStorage, so closing the tab ends it, and
    a watchdog closes it after INACTIVITY_MS of no activity. This is the case
    that matters on a shared or university machine, where the browser is left
    open and the next person inherits whatever is on screen.

  Everything that needs the token reads it here rather than from localStorage,
  so there is one answer to "am I signed in" no matter which of the two stores
  is holding it.
*/

const TOKEN_KEY = "numa_token";
const SEEN_KEY = "numa_last_seen";
const REASON_KEY = "numa_signed_out";

export const INACTIVITY_MS = 15 * 60 * 1000;

const session = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};
const local = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export function readToken() {
  // The short-lived store wins: signing in without "ricordami" must not be
  // overruled by a token an earlier remembered session left behind.
  return session()?.getItem(TOKEN_KEY) || local()?.getItem(TOKEN_KEY) || null;
}

export function saveToken(token, remember) {
  clearToken();
  const store = remember ? local() : session();
  try {
    store?.setItem(TOKEN_KEY, token);
  } catch {
    /* private browsing: the session lasts as long as the page does */
  }
  if (!remember) touch();
}

export function clearToken() {
  try {
    local()?.removeItem(TOKEN_KEY);
    session()?.removeItem(TOKEN_KEY);
    session()?.removeItem(SEEN_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** True while the token is held only for this tab - the one with a time limit. */
export function isTemporary() {
  return Boolean(session()?.getItem(TOKEN_KEY));
}

export function touch() {
  try {
    session()?.setItem(SEEN_KEY, String(Date.now()));
  } catch {
    /* the watchdog then works from the timer alone */
  }
}

/** Milliseconds since the last sign of life; 0 when unknown. */
export function idleFor() {
  const seen = Number(session()?.getItem(SEEN_KEY) || 0);
  return seen ? Date.now() - seen : 0;
}

/**
 * Why the person is looking at the sign-in form. Read once and cleared, so the
 * explanation appears on arrival and not again on the next visit.
 */
export function takeSignOutReason() {
  const reason = session()?.getItem(REASON_KEY) || "";
  try {
    session()?.removeItem(REASON_KEY);
  } catch {
    /* already gone */
  }
  return reason;
}

export function setSignOutReason(reason) {
  try {
    session()?.setItem(REASON_KEY, reason);
  } catch {
    /* the form simply says nothing */
  }
}


/*
  Passare la sessione a una scheda aperta da qui.

  Una sessione non «ricordata» vive in sessionStorage, che è per scheda: la
  seconda scheda - quella che «Vedi nel testo» apre - nasceva senza credenziali
  e finiva sulla pagina di accesso. Il token non può viaggiare nell'indirizzo,
  dove resterebbe nella cronologia e nei registri del server; viaggia invece da
  finestra a finestra, che è un canale dello stesso sito e non lascia tracce.
*/
const RICHIESTA = "numa:sessione:richiesta";
const RISPOSTA = "numa:sessione:risposta";

/** Da installare in ogni scheda: risponde a chi ha aperto da qui. */
export function rispondiAlleRichiesteDiSessione() {
  const ascolta = (evento) => {
    if (evento.origin !== window.location.origin) return;
    if (evento.data?.tipo !== RICHIESTA) return;
    evento.source?.postMessage(
      { tipo: RISPOSTA, token: readToken() },
      window.location.origin,
    );
  };
  window.addEventListener("message", ascolta);
  return () => window.removeEventListener("message", ascolta);
}

/** Chiede la sessione alla scheda che ha aperto questa. */
export function chiediSessioneAllApritore(attesaMs = 1500) {
  return new Promise((esito) => {
    const apritore = window.opener;
    if (!apritore || apritore.closed) return esito(null);

    let chiuso = false;
    const finisci = (token) => {
      if (chiuso) return;
      chiuso = true;
      window.removeEventListener("message", ascolta);
      clearTimeout(scadenza);
      esito(token || null);
    };
    const ascolta = (evento) => {
      if (evento.origin !== window.location.origin) return;
      if (evento.data?.tipo === RISPOSTA) finisci(evento.data.token);
    };
    window.addEventListener("message", ascolta);
    const scadenza = setTimeout(() => finisci(null), attesaMs);
    try {
      apritore.postMessage({ tipo: RICHIESTA }, window.location.origin);
    } catch {
      finisci(null);
    }
  });
}
